// src/pages/ExamInstructionsPage.jsx - REDESIGNED (Black & White Theme)
import React, { Suspense, lazy, useState } from 'react';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { BookOpen, PlayCircle, ArrowLeft, Home, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import NoExamSelected from './NoExamSelected.jsx';
import ExamModePicker from './ExamModePicker.jsx';

// The booklet's overview page loads with the paper exam's styles, only when needed.
const TestFormatSheet = lazy(() => import('../paperExam/TestFormatSheet.jsx'));

const PAPER_NOTES = ['booklet', 'blocks', 'handIn', 'results', 'speaking'];

/**
 * `paperSlug` (e.g. "telc-c1") when this exam has a paper version: the page
 * then offers Paper / Digital (`mode`, `onModeChange`) and, for paper, shows
 * the booklet's overview of the test instead of the section cards.
 */
const ExamInstructionsPage = ({ examData, onBeginExam, onBack, paperSlug = null, mode = 'digital', onModeChange }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [isBeginHovered, setIsBeginHovered] = useState(false);

  // Safety guard: Show message if no exam is selected
  if (!examData || !examData.examProvider || !examData.level) {
    return <NoExamSelected onBack={onBack} />;
  }

  return (
    <div className="bg-white min-h-screen flex flex-col relative overflow-hidden">
      {/* Compact Header - no top Back button */}
      <header className="relative flex-shrink-0 bg-white border-b border-gray-200 z-10">
        <div className="w-full max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-4 lg:px-8">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-black rounded-xl sm:rounded-2xl flex items-center justify-center text-white flex-shrink-0">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" />
              </div>
              <div className="min-w-0">
                <h1 className="font-space text-xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-black tracking-tight leading-tight truncate">
                  {examData.examName}
                </h1>
                <p className="text-sm sm:text-base lg:text-lg text-black/60">{t('exam.instructionsTitle')}</p>
              </div>
            </div>
          </div>
          <p className="text-sm sm:text-base lg:text-xl text-black/60 max-w-3xl mt-2 sm:mt-3">{t('exam.instructionsIntro')}</p>
        </div>
      </header>

      {/* Main Content - scrollable; padding-bottom clears fixed nav on mobile only */}
      <main
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-[calc(4rem+max(0.75rem,env(safe-area-inset-bottom,0px)))] md:pb-4"
      >
        <section className="py-4 sm:py-8 lg:py-12">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">

            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-4 sm:p-6 lg:p-10 border-2 sm:border-[3px] border-black/15">

              {paperSlug && onModeChange && (
                <ExamModePicker value={mode} onChange={onModeChange} className="mb-6 sm:mb-10" />
              )}

              {paperSlug && mode === 'paper' ? (
                <div className="grid grid-cols-1 gap-6 lg:gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] items-start">
                  <section aria-label={t('examMode.formatTitle')} className="min-w-0 rounded-2xl bg-[#525659] p-2 sm:p-5">
                    <Suspense fallback={<div className="aspect-[794/1123] w-full max-w-[794px] mx-auto rounded bg-white/80" />}>
                      <TestFormatSheet slug={paperSlug} />
                    </Suspense>
                  </section>
                  <aside className="min-w-0 lg:sticky lg:top-4">
                    <h2 className="font-space text-xl sm:text-2xl font-black text-black tracking-tight mb-4">{t('examMode.notes.title')}</h2>
                    <ul className="space-y-3">
                      {PAPER_NOTES.map((key) => (
                        <li key={key} className="flex gap-3 text-sm sm:text-base text-black/75 leading-snug">
                          <span className="flex-none mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black text-white" aria-hidden="true">
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </span>
                          <span>{t(`examMode.notes.${key === 'blocks' && String(paperSlug || '').startsWith('goethe') ? 'blocksGoethe' : key}`)}</span>
                        </li>
                      ))}
                    </ul>
                  </aside>
                </div>
              ) : (
              <>
              <h2 className="font-space text-xl sm:text-2xl lg:text-3xl font-black text-black mb-4 sm:mb-6 lg:mb-8 tracking-tight">{t('exam.sectionsTitle')}</h2>

              <div className="space-y-4 sm:space-y-6">
                {examData.sections.map((section, index) => (
                  <div
                    key={section.sectionId}
                    className="instruction-card group relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border-2 sm:border-[3px] border-black transition-all duration-300 ease-out"
                  >
                    <div className="instruction-shine-effect" aria-hidden />
                    <div className="instruction-card-number">
                      {String(index + 1).padStart(2, '0')}
                    </div>

                    <div className="relative z-10">
                      <h3 className="font-space font-black text-lg sm:text-2xl lg:text-3xl text-[#3A86FF] mb-3 sm:mb-4 transition-colors duration-500">
                        {section.sectionName}
                      </h3>
                      <div
                        className="prose prose-sm sm:prose-base lg:prose-lg max-w-none prose-gray transition-colors duration-500 prose-p:my-2 prose-ul:my-2 prose-li:my-0.5"
                        dangerouslySetInnerHTML={{ __html: marked.parse(section.instructions || '') }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              </>
              )}

              <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t-2 border-gray-100 text-center">
                <button
                  onClick={onBeginExam}
                  onMouseEnter={() => setIsBeginHovered(true)}
                  onMouseLeave={() => setIsBeginHovered(false)}
                  className="inline-flex items-center justify-center gap-2 sm:gap-3 px-6 py-3 sm:px-8 sm:py-4 bg-white rounded-xl border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-xl text-base sm:text-lg touch-manipulation min-h-[48px]"
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: 0,
                      width: isBeginHovered ? '100%' : '0%',
                      background: 'linear-gradient(to right, #60a5fa, #3b82f6, #1d4ed8)',
                      transition: 'width 0.7s ease-out',
                      zIndex: 0
                    }}
                  />
                  <PlayCircle
                    className="w-5 h-5 sm:w-6 sm:h-6 relative z-10 transition-colors duration-500"
                    style={{ color: isBeginHovered ? '#ffffff' : '#000000' }}
                  />
                  <span
                    className="font-space font-bold relative z-10 transition-colors duration-500"
                    style={{ color: isBeginHovered ? '#ffffff' : '#000000' }}
                  >
                    {t('exam.beginExam')}
                  </span>
                </button>
              </div>

            </div>
          </div>
        </section>
      </main>

      {/* Bottom Navigation Bar - mobile only */}
      <footer
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-[#E1E4E8]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <nav
          className="flex items-stretch justify-around h-16 px-2 max-w-lg mx-auto"
          aria-label="Anweisungen-Navigation"
        >
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
            aria-label={t('nav.home')}
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-xl hover:bg-gray-100">
              <Home className="w-[22px] h-[22px] text-[#6B7280]" strokeWidth={2} />
            </div>
            <span className="text-[10px] leading-none font-medium text-[#6B7280]">{t('nav.home')}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/exam')}
            className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
            aria-label={t('nav.exam')}
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-xl hover:bg-gray-100">
              <BookOpen className="w-[22px] h-[22px] text-[#6B7280]" strokeWidth={2} />
            </div>
            <span className="text-[10px] leading-none font-medium text-[#6B7280]">{t('nav.exam')}</span>
          </button>
          <button
            type="button"
            onClick={onBack}
            className="flex flex-col items-center justify-center gap-0.5 px-2 min-w-[56px] min-h-[44px] transition-all duration-200 touch-manipulation active:scale-95"
            aria-label={t('common.goBack')}
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-[#006D77]/10">
              <ArrowLeft className="w-[22px] h-[22px] text-[#006D77]" strokeWidth={2.5} />
            </div>
            <span className="text-[10px] leading-none font-medium text-[#006D77]">{t('common.goBack')}</span>
          </button>
        </nav>
      </footer>

      {/* Custom CSS Block */}
      <style>{`
        .instruction-card {
          position: relative;
          overflow: hidden; 
        }
        .instruction-card:hover {
          transform: scale(1.03) translateY(-6px);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
          background: linear-gradient(to bottom right, #000, #1a1a1a);
        }

        .instruction-card-number {
          position: absolute;
          top: 0.75rem;
          right: 1rem;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 3rem;
          font-weight: 900;
          color: #3A86FF;
          opacity: 0.15;
          line-height: 1;
          transition: all 0.5s ease;
          z-index: 6;
        }
        @media (min-width: 640px) {
          .instruction-card-number {
            top: 1rem;
            right: 1.5rem;
            font-size: 4rem;
          }
        }
        @media (min-width: 1024px) {
          .instruction-card-number {
            font-size: 5rem;
          }
        }
        .instruction-card:hover .instruction-card-number {
          opacity: 0.25;
          color: #ffffff;
        }

        .instruction-shine-effect {
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            transition: left 0.7s ease;
            z-index: 5; 
        }
        .instruction-card:hover .instruction-shine-effect {
            left: 100%;
        }

        /* Text color changes on hover */
        .instruction-card:hover h3 {
          color: #ffffff;
        }
        .instruction-card:hover .prose {
          color: rgba(255, 255, 255, 0.8);
        }
        .instruction-card:hover .prose strong,
        .instruction-card:hover .prose h3,
        .instruction-card:hover .prose h4 {
          color: #ffffff;
        }
        .instruction-card:hover .prose li::marker {
            color: #3A86FF;
        }
      `}</style>
    </div>
  );
};

export default ExamInstructionsPage;